import { useEffect, useState } from 'react'
import { useTar } from '@/modules/i18n'
import { Progress } from '@duck/ui'

interface WorkspaceInitializationProps {
    isBooting: boolean
}

export const WorkspaceInitialization = ({ isBooting }: WorkspaceInitializationProps) => {
    const { t } = useTar()
    const [progress, setProgress] = useState(0)
    const [stage, setStage] = useState<'creating' | 'populating' | 'initializing'>('creating')
    const [startTime] = useState(Date.now())

    // Exponential easing function: slows down exponentially as it approaches target
    const exponentialEase = (t: number, target: number, start = 0): number => {
        if (t <= 0) return start
        // Use exponential decay: progress = target - (target - start) * e^(-k*t)
        // Higher k value = faster initial progress, slower near end
        const k = 0.35 // Controls the exponential rate
        const progress = target - (target - start) * Math.exp(-k * t)
        return Math.min(progress, target)
    }

    useEffect(() => {
        if (isBooting) {
            const interval = setInterval(() => {
                const elapsedSeconds = (Date.now() - startTime) / 1000 // seconds

                if (elapsedSeconds < 2.5) {
                    // Stage 1: Creating Challenge Database (0-2.5s) - linear
                    setStage('creating')
                    setProgress(12 * elapsedSeconds) // 12% per second, max 30% at 2.5s
                } else if (elapsedSeconds < 5.5) {
                    // Stage 2: Populating Data (2.5-5.5s) - linear
                    setStage('populating')
                    const stageElapsed = elapsedSeconds - 2.5 // Time in this stage
                    const start = 47
                    setProgress(start + 6 * stageElapsed) // 6% per second from 47%, max 65% at 5.5s
                } else {
                    // Stage 3: Initializing Workspace (5.5s+) - exponential slowdown
                    setStage('initializing')
                    const stageElapsed = elapsedSeconds - 5.5 // Time in this stage
                    const start = 78
                    const target = 100 // Final target
                    setProgress(exponentialEase(stageElapsed, target, start))
                }
            }, 100) // Update every 100ms for smooth animation

            return () => clearInterval(interval)
        } else {
            // Not booting: just show Initializing Workspace with exponential easing
            setStage('initializing')
            const interval = setInterval(() => {
                const elapsed = (Date.now() - startTime) / 1000 // seconds
                setProgress(exponentialEase(elapsed, 100, 15))
            }, 100)

            return () => clearInterval(interval)
        }
    }, [isBooting, startTime])

    const getStageText = () => {
        if (isBooting) {
            switch (stage) {
                case 'creating':
                    return t('challenges.creatingDatabase')
                case 'populating':
                    return t('challenges.populatingData')
                case 'initializing':
                    return t('challenges.initializingWorkspace')
            }
        }
        return t('challenges.initializingWorkspace')
    }

    return (
        <div className="flex items-center justify-center w-full h-[66vh]">
            <div className="text-center space-y-4 w-full max-w-md px-4">
                {/* <StandardLoader size="lg" /> */}
                <div className="space-y-2">
                    <h2 className="text-xl font-semibold text-foreground">
                        {t('challenges.startingChallenge')}
                    </h2>
                    <p className="text-muted-foreground">
                        {getStageText()}
                    </p>
                </div>
                <Progress value={progress} className="mt-4 h-1" />
            </div>
        </div>
    )
}

